import { useState, type ReactNode } from "react";
import { Button } from "./primitives";

/**
 * The one-line box you type into: Enter sends, Shift+Enter breaks the line, the button
 * is disabled until there is something to send. The Feed's comment form lifted out
 * (2026-09-19) so a Goon Room's chat types the same way. `onSend` does the sending —
 * the composer only knows text; its promise settling is what re-enables the box, and
 * a rejection's message is shown under it. `error` lets the owner show a refusal that
 * arrived some other way (a socket frame).
 */
export function MessageComposer({
  onSend,
  maxLength,
  placeholder,
  submitLabel = "Send",
  leading,
  onCancel,
  autoFocus,
  disabled,
  error,
}: {
  onSend: (text: string) => Promise<void> | void;
  maxLength: number;
  placeholder: string;
  submitLabel?: string;
  /** A button before the box — a room's attach-an-image. */
  leading?: ReactNode;
  onCancel?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: string | null;
}) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const send = async () => {
    const text = body.trim();
    if (!text || busy || disabled) return;
    setBusy(true);
    setLocalError(null);
    try {
      await onSend(text);
      setBody("");
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "";
      setLocalError(msg || "That didn't land. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const shown = error ?? localError;
  return (
    <div>
      <div className="flex items-stretch gap-2">
        {leading}
        <textarea
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          rows={1}
          maxLength={maxLength}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          className="min-h-9 flex-1 resize-none rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] px-3 py-1.5 text-base text-zinc-100 placeholder:text-zinc-600 disabled:opacity-50 sm:text-sm"
        />
        <Button size="sm" variant="primary" disabled={!body.trim() || busy || disabled} onClick={() => void send()}>
          {busy ? "…" : submitLabel}
        </Button>
        {onCancel ? (
          <Button size="sm" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
      {shown ? <p className="mt-1 text-xs text-red-400">{shown}</p> : null}
    </div>
  );
}
